import React from 'react';
import { useMediaQuery } from 'react-responsive';
import { makeStyles } from '@material-ui/core/styles';
import TrendingUpRoundedIcon from '@material-ui/icons/TrendingUpRounded';
import TrendingDownRoundedIcon from '@material-ui/icons/TrendingDownRounded';
import Chart from 'react-apexcharts';
import { CircularProgress } from '@material-ui/core';
import PropTypes, { string, bool } from 'prop-types';
import numberWithCommas from '../../functions';
import chartOptions from '../../customUI/ChartOptions';
import { storeContext } from '../provider/Provider';
import Tooltip from '../tooltip/Tooltip';
import CONSTANTS from '../../utils/constants';

const InfoCard = ({
  name,
  explanation,
  value,
  percentage,
  isFetching,
  historyValues,
  labels,
  chartType,
  chartColor,
  tooltip,
  reversed,
  centered,
  inline,
  inlineTooltip,
}) => {
  const { state } = React.useContext(storeContext);
  let chartWidth = '100%';

  const lineOptions = {
    ...chartOptions,
    labels,
    tooltip: {
      x: {
        show: false,
      },
      y: {
        title: {
          formatter: () => '',
        },
      },
    },
  };
  if (chartType === 'bar') {
    chartWidth = '100%';
    lineOptions.tooltip.x.show = true;
  }
  const isLaptop = useMediaQuery({ query: '(max-width: 1950px)' });
  lineOptions.plotOptions.bar.columnWidth = '80%';
  lineOptions.plotOptions.bar.borderRadius = 0;
  lineOptions.stroke = {
    curve: 'smooth',
    width: 2,
  };
  lineOptions.xaxis = {
    labels: { show: false },
    axisTicks: { show: false },
    axisBorder: { show: false },
  };
  lineOptions.yaxis = {
    labels: { show: false },
    axisTicks: { show: false },
    axisBorder: { show: false },
  };
  lineOptions.grid = { show: false };
  lineOptions.colors = [chartColor];
  let iconColorBasedOnDirection = '#D81F27';
  if (percentage >= 0.0) {
    iconColorBasedOnDirection = '#5AA600';
  }

  let wrapperClass = {};
  if (inline) {
    wrapperClass = {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };
  } else {
    wrapperClass = {
      width: '100%',
    };
  }

  if (centered) {
    wrapperClass.textAlign = 'center';
  }

  const cardMinHeight = reversed && centered ? 130 : 140;

  const useStyles = makeStyles((theme) => ({
    card: {
      position: 'relative',
      background: 'white',
      borderRadius: '16px',
      padding: reversed && centered ? '10px' : '24px 24px 20px 24px',
      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
      minHeight: inline ? 90 : cardMinHeight,
      [theme.breakpoints.down('sm')]: {
        margin: 10,
        padding: 10,
        minHeight: 0,
      },
    },
    iconUp: {
      fontSize: 'large',
      marginRight: 5,
      verticalAlign: 'middle',
      background: '#D6F3B3',
      borderRadius: '50%',
      padding: 2.5,
      fill: '#5AA600',
      [theme.breakpoints.down('sm')]: {
        fontSize: 'small',
      },
    },
    iconDown: {
      fontSize: 'large',
      marginRight: 5,
      verticalAlign: 'middle',
      background: '#F9BBC0',
      borderRadius: '50%',
      padding: 2.5,
      fill: 'D81F27',
      [theme.breakpoints.down('sm')]: {
        fontSize: 'small',
      },
    },
    percentage: {
      color: iconColorBasedOnDirection,
      fontSize: '16px',
      marginTop: 10,
      [theme.breakpoints.down('sm')]: {
        fontSize: '15px',
      },
    },
    name: {
      fontSize: reversed && centered ? 16 : 25,
      color: '#58595B',
      margin: 0,
      minHeight: reversed && centered ? 42 : 48,
      whiteSpace: inline ? 'nowrap' : 'normal',
      display: centered ? 'flex' : 'block',
      flex: inline ? 'auto 1' : 'unset',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      [theme.breakpoints.down('sm')]: {
        fontSize: 16,
        minHeight: 0,
      },
    },
    explanation: {
      fontSize: 17,
      color: '#58595B',
      margin: 0,
      minHeight: 38,
      [theme.breakpoints.down('sm')]: {
        fontSize: 15,
        minHeight: 0,
      },
    },
    value: {
      fontWeight: 600,
      fontSize: 30,
      margin: 0,
      marginTop: 5,
      fontFamily: 'Roboto',
      [theme.breakpoints.down('sm')]: {
        fontSize: 22,
      },
    },
    flex: {
      display: 'flex',
      flexWrap: 'nowrap',
      justifyContent: 'space-between',
    },
    laptopGraph: {
      maxWidth: '45%',
    },
    reversed: {
      display: 'flex',
      flexWrap: 'nowrap',
      flexDirection: 'column-reverse',
      alignItems: reversed === 'align-left' ? 'flex-start' : 'center',
      width: '100%',
    },
    textCentered: {
      textAlign: 'center',
    },
    wrapperClass,
  }));
  const classes = useStyles();
  const isPhone = useMediaQuery({ query: '(max-width: 768px)' });
  return (
    <div className={classes.card}>
      <div className={classes.flex}>
        <div className={classes.wrapperClass}>
          <div className={classes.name}>
            <span>
              {name}{' '}
              {!isPhone && inlineTooltip && (
                <Tooltip style={{ display: 'inline-block', marginLeft: 10 }} text={tooltip} />
              )}
            </span>
          </div>
          <div className={reversed ? classes.reversed : ''}>
            <div className={classes.percentage}>
              {percentage ? (
                <>
                  {percentage >= 0.0 && (
                    <span>
                      <TrendingUpRoundedIcon className={classes.iconUp} /> +
                    </span>
                  )}
                  {percentage < 0.0 && (
                    <span>
                      <TrendingDownRoundedIcon className={classes.iconDown} />{' '}
                    </span>
                  )}
                  {percentage}%{' '}
                  <Tooltip style={{ display: 'inline-block', marginLeft: 10 }} text={CONSTANTS.PercentageTooltipText} />
                  {explanation ? <span style={{ color: '#58595B' }}>{explanation}</span> : ''}
                  <br />
                </>
              ) : (
                <p className={classes.explanation}>{explanation}</p>
              )}
            </div>
            {isFetching ? (
              <CircularProgress style={{ color: '#109CF1', width: '15px', height: '15px' }} />
            ) : (
              <p className={classes.value}>{numberWithCommas(value)}</p>
            )}
          </div>
        </div>
        {!isLaptop ? (
          <div>
            {historyValues[0].data.length !== 0 && (
              <>
                {!state.websiteAnalytics.isTesting && (
                  <Chart
                    options={lineOptions}
                    series={historyValues}
                    type={chartType}
                    height={isPhone ? '80' : '120'}
                    width={chartWidth}
                  />
                )}
              </>
            )}
          </div>
        ) : (
          <div className={classes.laptopGraph}>
            {historyValues[0].data.length !== 0 && (
              <>
                {!state.websiteAnalytics.isTesting && (
                  <Chart
                    options={lineOptions}
                    series={historyValues}
                    type={chartType}
                    height={isPhone ? '80' : '120'}
                    width={chartWidth}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
      {!isPhone && !inlineTooltip && <Tooltip text={tooltip} />}
    </div>
  );
};

InfoCard.propTypes = {
  name: string.isRequired,
  value: string.isRequired,
  percentage: string.isRequired,
  isFetching: bool.isRequired,
  historyValues: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.arrayOf(string),
    })
  ),
  labels: PropTypes.arrayOf(string),
  explanation: string,
  chartType: string,
  chartColor: string,
  tooltip: string,
  reversed: string,
  centered: bool,
  inline: bool,
  inlineTooltip: bool,
};
InfoCard.defaultProps = {
  historyValues: [{ data: [] }],
  labels: [],
  explanation: '',
  chartType: 'line',
  chartColor: '#109CF1',
  tooltip: '-',
  reversed: '',
  centered: false,
  inline: false,
  inlineTooltip: false,
};
export default InfoCard;
